/* eslint-disable no-plusplus */
/* eslint-disable no-multiple-empty-lines */
import Big from 'big.js'
import qs from 'qs'
import moment from 'moment-timezone'
import validator from 'validator'
// Helpers
import bobtailAPI from './axiosConfig'
import {
  ApiService,
  ClientDocument,
  DebtorDocument,
} from './api'
import FactoringAPI from './FactoringAPI'
import {
  captureSentryEvent,
  captureSentryTransaction,
} from './sentryMixin'
import {
  CError,
  CLog,
} from './consoleMixin'
import {
  convertBigToDollars,
} from '../../../shared/calculations'
import {
  statesAbbreviations,
  canadianStates,
} from './constants'
import FlagMappings from '../../../shared/FlagMappings.json'
import { mapFlag } from '../../../shared/mapFlags'


export const getAchHours = async () => {
  const ACH_DATA = (await ApiService.getConfig({
    keys: 'FIRST_ACH_HOURS,FIRST_ACH_TIME,SECOND_ACH_HOURS,SECOND_ACH_TIME,FIRST_ACH_CUTOFF_HOURS,SECOND_ACH_CUTOFF_HOURS',
  })).data

  return {
    // when first transfer goes out
    firstAchHours: ACH_DATA['FIRST_ACH_HOURS'],
    firstAchTime: ACH_DATA['FIRST_ACH_TIME'],
    // second transfer
    secondAchHours: ACH_DATA['SECOND_ACH_HOURS'],
    secondAchTime: ACH_DATA['SECOND_ACH_TIME'],
    // cutoff for transfer buckets
    firstAchCutoffHours: ACH_DATA['FIRST_ACH_CUTOFF_HOURS'],
    secondAchCutoffHours: ACH_DATA['SECOND_ACH_CUTOFF_HOURS'],
  }
}

export const debounce = (func, wait, immediate = null) => {
  let timeout
  return () => {
    const context = this
    const later = () => {
      timeout = null
      if (!immediate) {
        func.apply(context)
      }
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) {
      func.apply(context)
    }
  }
}

export const requestWithTransaction = async (transactionName, opName, callbackFn =  () => {}) => {
  const transaction = captureSentryTransaction(transactionName, opName)
  const response = await callbackFn()
  if (transaction) {
    transaction.finish()
  }
  return response
}






// DATE AND TIME, TEXT
// Strips cents off of dollar values for presentation
export const cleanCents = (pennies) => {
  return Intl
    .NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 })
    .format(pennies.div(100).toFixed(0))
}

// Strips out commas from text / Use anytime reading input from an amount input, such as primary rate
// includeSign only works if there is a "$" in the text being cleaned
export const cleanUserInput = (input, includeSign = false) => {
  if (input === null) {
    return '0'
  }
  if (typeof input === 'number') {
    return input
  }

  // Removes everything but periods and numbers
  const inputWithoutWhitespace = input.replaceAll(' ', '')
  const regex = includeSign
    ? /[^0-9.](?!\$)/g
    : /[^0-9.]/g
  let cleanedInput = inputWithoutWhitespace.replace(regex, '')

  // Inputs that start with '.' are really '0.'
  if (cleanedInput[0] === '.') {
    cleanedInput = `0${cleanedInput}`
  }

  return cleanedInput
}

export const exportSortedMappingsKeys = (status, type, mappings = FlagMappings) => {
  return Object
    .keys(mappings)
    .filter((key) => {
      const mapping = mappings[key]
      return mapping.status.includes(status) && mapping.type.includes(type)
    })
    .sort((keyA, keyB) => {
      const longA = mapFlag(
        keyA,
        {
          from: 'enum',
          to: 'long',
          status: status,
        }
      ).toLowerCase()
      const longB = mapFlag(
        keyB,
        {
          from: 'enum',
          to: 'long',
          status: status,
        }
      ).toLowerCase()

      if (longA < longB) {
        return -1
      }
      else if (longA > longB) {
        return 1
      }
      else {
        return 0
      }
    })
}

// Unused, but foreseeable in the future
export const formatAddressCamelCase = (address, address_2 = null, city, state, zip) => {
  const address2 = address_2
    ? `, ${address_2}`
    : ''
  const completeAddress = `${address}${address2}, ${city}, ${state} ${zip}`.toLowerCase()

  // uppercase first char and any char immediately following a space
  let capitalizeNext = false
  const formattedAddress = Array.from(completeAddress.trim()).map((char, index) => {
    let transformedChar = char

    // Capitalize first index in the address
    if (index === 0) {
      transformedChar = char.toUpperCase()
    }

    // If the previous char was ' ' or '.'
    if (capitalizeNext) {
      capitalizeNext = false
      transformedChar = char.toUpperCase()
    }

    // If the current char is ' ' or '.', capitalize the next char
    if ([ ' ', '.' ].includes(char)) {
      capitalizeNext = true
    }

    return transformedChar
  }).join('')
  return formattedAddress
}

export const formatDollars = (big) => {
  return new Intl
    .NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(convertBigToDollars(big))
}

export const formatPhoneNumber = (phoneNumberString) => {
  if (!phoneNumberString) {
    return null
  }
  const cleaned = (phoneNumberString.trim()).replace(/\D/g, '')
  const match = cleaned.match(/^(1?)(\d{3})(\d{3})(\d{4})(\d+)*$/)
  if (match) {
    let formattedNumber = `${match[2]}-${match[3]}-${match[4]}`
    if (match[5] !== undefined) {
      formattedNumber += ` x${match[5]}`
    }
    return formattedNumber
  }
  return null
}

export const formatStringCamelCase = (string) => {
  if (!string) {
    return null
  }
  // uppercase first char and any char immediately following a space
  let capitalizeNext = false
  const formattedString = Array.from(string.trim().toLowerCase()).map((char, index) => {
    let transformedChar = char
    // Capitalize first index in the address
    if (index === 0) {
      transformedChar = char.toUpperCase()
    }
    // If the previous char was ' ' or '.'
    if (capitalizeNext) {
      capitalizeNext = false
      transformedChar = char.toUpperCase()
    }
    // If the current char is ' ' or '.', capitalize the next char
    if ([ ' ', '.' ].includes(char)) {
      capitalizeNext = true
    }
    return transformedChar
  }).join('')
  return formattedString
}

export const getOneYearFromDate = (date) => {
  return moment(date)
    .subtract(1, 'years')
    .toDate()
}

export const getPreviousMonthEnd = () => {
  return moment
    .tz('America/New_York')
    .subtract(1, 'months')
    .toDate()
}

export const getPreviousMonthStart = () => {
  return moment
    .tz('America/New_York')
    .startOf('month')
    .subtract(1, 'months')
    .toDate()
}

export const getThisMonthEnd = () => {
  return moment
    .tz('America/New_York')
    .toDate()
}

export const getThisMonthStart = () => {
  return moment
    .tz('America/New_York')
    .startOf('month')
    .toDate()
}

export const sanitizeAlgoliaNames = (name) => {
  const regex = /(<em>)*(<\/em>)*/gi
  // if name is null do not throw an error
  return name && name.replace(regex, '')
}

export const getActiveTransferBucket = async (testing = false) => {
  const {
    firstAchCutoffHours,
    firstAchTime,
    secondAchCutoffHours,
    secondAchTime,
  } = await getAchHours()
  // eslint-disable-next-line max-len
  moment.tz.add('America/New_York|EST EDT EWT EPT|50 40 40 40|01010101010101010101010101010101010101010101010102301010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010101010|-261t0 1nX0 11B0 1nX0 11B0 1qL0 1a10 11z0 1qN0 WL0 1qN0 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1qN0 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1qN0 WL0 1qN0 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1qN0 WL0 1qN0 11z0 1o10 11z0 RB0 8x40 iv0 1o10 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1qN0 WL0 1qN0 11z0 1o10 11z0 1o10 11z0 1o10 11z0 1o10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1fz0 1a10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1fz0 1cN0 1cL0 1cN0 1cL0 s10 1Vz0 LB0 1BX0 1cN0 1fz0 1a10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 1cN0 1fz0 1a10 1fz0 1cN0 1cL0 1cN0 1cL0 1cN0 1cL0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|21e6')
  const currentTimeInNYC = testing
    ? moment(testing)
    : moment().tz('America/New_York')
  CLog(`%c Current time in NYC: ${currentTimeInNYC}`, 'color: green; font-size: 16px;')
  // If between 1 hour before FIRST_ACH_HOURS and 1 hour before SECOND_ACH_HOURS
  const currentHour = currentTimeInNYC.get('hour')
  if (firstAchCutoffHours <= currentHour && currentHour < secondAchCutoffHours) {
    return secondAchTime
  }
  return firstAchTime
}

export const XSSProtection = (input) => {
  return validator.blacklist(input, '<>')
}





// COMPONENT DATA MANIPULATION
export const changeCountryCallback = (componentData) => {
  componentData.$set(
    componentData.state,
    'options',
    componentData.country.value === 'US'
      ? statesAbbreviations
      : canadianStates
  )
  componentData.$set(componentData.address, 'value', '')
  componentData.$set(componentData.address2, 'value', '')
  componentData.$set(componentData.city, 'value', '')
  componentData.$set(componentData.state, 'value', '')
  componentData.$set(componentData.zip, 'value', '')
}






// CSS MANIPULATION
export const calculateLeftPosition = (element) => {
  const currentWindowWidth = window.innerWidth
  const elementWidth = element.offsetWidth

  return `${(currentWindowWidth - elementWidth) / 2}px`
}

export const calculateTopPosition = (index, spacing, previousTop = 0) => {
  const top = index === 0
    ? spacing
    : previousTop + spacing

  return `${window.matchMedia('(max-width: 1024px)')
    ? top
    : top - (top / 2)
  }px`
}

// Mutates a DOM element, should be A-okay since it's not data
// Turns pixel heights into rems
export const setWrapperHeight = (el) => {
  const wrapper = document.querySelector(el)
  wrapper.style.height = null // reset
  wrapper.style.height = `${wrapper.clientHeight / 16}rem`
}






// DATA
// optionalAppendedFilterString allows us to append complex combinations of AND, OR, NOT to
// the end of our filter string for Algoila, because this function isn't that robust
export const buildAlgoliaFilterString = (filters, optionalAppendedFilterString = ' ') => {
  const keys = Object.keys(filters)
  const filteredKeys = keys.filter((key) => {
    // If key value is not null, an empty string, or is an array && has no length, keep it
    const value = filters[key]

    return (
      value !== null
      && value !== ''
      && !(Array.isArray(value) && !value.length)
    )
  })
  // If the value is NOT an array, join key and value with ":"
  // If value is an array, join array
  const algoliaFilterString = filteredKeys
    .map((key) => {
      // accept multiple possible values for a field
      if (key.includes('or___')) {
        const keyName = key.replace('or___', '')
        return `(${keyName}:${filters[key].join(` OR ${keyName}:`)})`
      }

      // Key value is an array
      // Algolia filters: https://www.algolia.com/doc/api-reference/api-parameters/filters
      // Specifically for days outstanding from/to in approved-search
      if (Array.isArray(filters[key])) {
        return filters[key].length > 1
          ? `(${filters[key].join(' AND ')})`
          : filters[key][0]
      }
      // Key value is not an array
      return [ key, filters[key] ].join(':')
    })
    .join(' AND ')
    .concat(optionalAppendedFilterString)
  CLog(`%c Algolia filter string: ||${algoliaFilterString}||`, 'color: blue;')
  return algoliaFilterString
}

export const calculatePercentDifference = (newValue, oldValue) => {
  // NOTE: params are expected as big instances of penny values

  // Since we're using big.js, we can't instantiate percent because of possible
  // divisions by 0, which throws a big error
  let percent

  // if a and b are 0, return 0
  // If b (old) is 0, dividing would give undefined, so return a as a percent
  if (oldValue.eq(0)) {
    percent = newValue.div(100)
  }

  // If a (new) is 0 and b isn't 0, returns 100
  else {
    percent = newValue.minus(oldValue).div(oldValue)
  }

  return percent
}

export const calculatePercentDifferencePositivityAndText = (newTotal, oldTotal) => {
  const percent = calculatePercentDifference(newTotal, oldTotal)

  return {
    positivity: percent.cmp(0),
    text: new Intl
      .NumberFormat('en-US', { style: 'percent' })
      .format(Math.abs(percent)),
  }
}

// Handles deep objects via qs
export const readQueryStringToObject = (queryString) => {
  return qs.parse(queryString)
}

export const stringifyObjectToQueryString = (obj) => {
  // Remove null/empty values
  const cleanedObj = {}
  Object.keys(obj).forEach((key) => {
    if (
      obj[key] === ''
      || obj[key] === null
      || obj[key] === undefined
      || obj[key] === false
    ) {
      return
    }
    cleanedObj[key] = obj[key]
  })
  // Meant for production (logs filters for paid and declined search)
  return qs.stringify(cleanedObj)
}

// replace links to duplicate invoices with html so the user never sees {{{loadnumber:id}}}
const transformInvoiceUpdates = (updates) => {
  if (!updates) {
    return null
  }
  const invoice_updates = [ ...updates ]
  invoice_updates.forEach((update) => {
    // Duplicate links in individual updates: https://github.com/fs-bobtail/bobtail/pull/802
    if (update.metadata && update.metadata.possible_duplicates) {
      update.duplicate_links = []
      update.metadata.possible_duplicates.forEach((dupe, index) => {
        update.duplicate_links.push({
          comma: index !== update.metadata.possible_duplicates.length - 1,
          invoice_id: dupe.id,
          load_number: dupe.load_number,
          status: dupe.status,
        })
      })
    }
    // for nonpayments the update_status & notes should be displayed in reverse
    // https://github.com/fs-bobtail/bobtail/pull/856
    if (update.update_type === 'nonpayment') {
      const note = mapFlag(update.update_status, {
        from: 'enum',
        to: 'long',
        status: update.update_type,
      })
      update.notes = note.replace('.', '').charAt(0).toUpperCase()
      + note.slice(1)

      update.update_status = 'Nonpayment'
    }
  })
  return invoice_updates
}

/*
 * Why we transform:
 * 1. Simplifies JS in component templates, mostly by avoiding tertiary statements and diving
 *    numbers by 100
 * 2. base-invoice-create requires specifically formatted props to hydrate the component
 * 3. B.E doesn't delivery user-friendly amounts, so everytime an amount would be needed, the
 *    F.E. would have to divide by 100
 * 4. The B.E. uses poor property naming
 * 5. B.E. doesn't deliver certain calculated values, such as daysApproved, overpaid, shortpaid
 * 6. B.E. is not consistent on a property's existence or value when no value exists for said prop
 *    which means it is sometimes missing on the invoice object, null, '', or false
 * 7. Invoice updates need to be formatted for certain use cases, such as duplicate links
 */
export const transformRawInvoice = (invoice) => {
  // If invoice has already been trasnformed, skip
  if (invoice.transformed) {
    return invoice
  }

  const last_flag_message = [ ...new Set((invoice.last_flag_message || '').split(', ')) ]
    .join(', ')
  const daysApproved = invoice.is_buyout
    ? (moment().startOf('day')).diff(moment(invoice.original_buyout_invoice_date).startOf('day'), 'days')
    : (moment().startOf('day')).diff(moment(invoice.approved_date).startOf('day'), 'days')
  // invoice.total_amount is in pennies
  // https://bobtail.atlassian.net/browse/SM-494
  const totalAmount = new Big(invoice.total_amount)
  // Paid invoices only
  let overpaid = null
  let shortpaid = null

  if (invoice.debtor_payment && invoice.debtor_payment.length) {
    // debtor payment amount (cur.amount) is in pennies
    const totalDebtorPaid = new Big(invoice.debtor_payment.reduce((acc, cur) => {
      return acc + cur.amount
    }, 0))
    // big.js calculations
    // https://bobtail.atlassian.net/browse/SM-494
    overpaid = totalDebtorPaid.gt(totalAmount)
      ? totalDebtorPaid.minus(totalAmount)
      : new Big(0)
    shortpaid = totalDebtorPaid.lt(totalAmount)
      ? totalAmount.minus(totalDebtorPaid)
      : new Big(0)
  }

  return {
    ...invoice,
    // https://bobtail.atlassian.net/browse/SM-494
    amounts: {
      advance: invoice.advance
        ? new Big(invoice.advance)
        : new Big(0),
      client_total_to_pay_from_invoice: invoice.client_total_to_pay_from_invoice
        ? new Big(invoice.client_total_to_pay_from_invoice)
        : new Big(0),
      detention: invoice.detention
        ? new Big(invoice.detention)
        : new Big(0),
      lumper: invoice.lumper
        ? new Big(invoice.lumper)
        : new Big(0),
      overpaid,
      primary_rate: new Big(invoice.primary_rate),
      shortpaid,
      total: totalAmount,
    },
    // We only care about how many days since approved, not when an invoice was approved
    days_approved: daysApproved,
    // Backend doesn't ALWAYS delivery this property on the invoice
    debtor_payment: invoice.debtor_payment
      ? invoice.debtor_payment
      : null,
    // Backend doesn't ALWAYS delivery this property on the invoice
    first_debtor_payment: invoice.first_debtor_payment
      ? invoice.first_debtor_payment
      : null,
    // algolia isn't always quick to deliver the correct value
    last_flag_message,
    // Backend doesn't ALWAYS delivery this property on the invoice
    original_buyout_invoice_date: invoice.original_buyout_invoice_date
      ? new Date(invoice.original_buyout_invoice_date)
      : null,
    // Backend doesn't ALWAYS delivery this property on the invoice
    paid_date: invoice.paid_date
      ? invoice.paid_date
      : null,
    // Used on frontend to avoid transforming an already trasnformed invoice
    transformed: true,
    updates: transformInvoiceUpdates(invoice.invoice_updates),
  }
}






// DECISIONS
export const isMobile = () => {
  return window.matchMedia('(max-width: 1024px)').matches
}

export const shouldInsertInvoiceUponCreate = async (args, testing = false) => {
  /*
    * Five criteria:
    * 1) Number of current search results (count)
    * 2) Type of user (user)
    * 3) Invoice's tranfer type (type)
    * 4) Current time (time)
    * 5) Current searched for transfer (searched transfer)
    *
    * Checks should be performed in this order
    *
    * IF (count >= 25)                                      DO NOT DISPLAY
    * --------------------------------------------------------------------
    * ELSE IF (user === 'client')                                DISPLAY
    * --------------------------------------------------------------------
    * ELSE IF (type === 'ach')
    * --------------------------------------------------------------------
    * IF (searched for transfer === 'wire')                 DO NOT DISPLAY
    * --------------------------------------------------------------------
    * IF (searched transfer === 'firstAchTime' && time === 10:00     DISPLAY
    * --------------------------------------------------------------------
    * IF (searched transfer === 'firstAchTime' && time === 11:00     DO NOT DISPLAY
    * --------------------------------------------------------------------
    * IF (searched transfer === 'firstAchTime' && time === 19:00     DISPLAY
    * --------------------------------------------------------------------
    * IF (searched transfer === 'firstAchTime' && time === 21:30     DISPLAY
    * --------------------------------------------------------------------
    * IF (searched transfer === 'secondAchTime' && time === 12:30     DISPLAY
    * --------------------------------------------------------------------
    * IF (searched transfer === 'secondAchTime' && time === 18:00     DISPLAY
    * --------------------------------------------------------------------
    * IF (searched transfer === 'secondAchTime' && time === 19:00     DO NOT DISPLAY
    * --------------------------------------------------------------------
    * ELSE IF (type === 'wire')
    * --------------------------------------------------------------------
    * IF (searched for transfer === 'ach')                  DO NOT DISPLAY
    * --------------------------------------------------------------------
    * IF (searched for transfer === 'wire')                 DISPLAY
    * --------------------------------------------------------------------
  */

  if (args.searchIDs.length >= 25) {
    return false
  }
  // Clients can't view invoice-details, so it doesn't matter what the current searched transfer time is
  else if (args.userIsClient) {
    return true
  }
  // IDs always have length in this scenario because base-invoice-create only
  // exists on pending invoices
  else {
    const { firstAchTime, secondAchTime } = await getAchHours()
    if (args.searchParams.transfer_type === 'ach') {
      if (args.invoice.transfer_type === 'wire') {
        return false
      }

      const transferCuttoffTimeValue = await getActiveTransferBucket(testing)

      // IF the current SEARCH transfer time is firstAchTime
      if (args.searchParams.transfer_time === firstAchTime) {
        // IF this invoice is created within the firstAchTime transfer time window (EST)
        if (transferCuttoffTimeValue === firstAchTime) {
          return true
        }
        else {
          return false
        }
      }

      // IF the current SEARCH transfer time is secondAchTime
      if (args.searchParams.transfer_time === secondAchTime) {
        // IF this invoice is created within the secondAchTime transfer time window (EST)
        if (transferCuttoffTimeValue === secondAchTime) {
          return true
        }
        else {
          return false
        }
      }
    }
    // args.searchParams.transfer_type === 'wire'
    else {
      // If this invoice is created as an expedite
      if (args.invoice.transfer_type === 'wire') {
        return true
      }
      else {
        return false
      }
    }
  }
}





// USER
export const storeUserInLocalStorage = async () => {
  // Set JWT token in localStorage so bobtailAPI will have it for BASE_API()
  try {
    const userInfo = (await (await bobtailAPI()).get('/api/users/me')).data
    window.localStorage.setItem('user', JSON.stringify(userInfo))
    CLog(
      '%c User stored in localStorage',
      'color: green;font-size: 18px; font-weight: 600;'
    )
  }
  catch (error) {
    captureSentryEvent(
      'Helpers: storeUserInLocalStorage',
      {
        config: error.config,
        details: error,
        response: error.response,
        message: 'Most likely due to user login being valid with Auth0 but not existing in our DB',
      }
    )
    CLog('storedIn', error)
    // Don't let user go anywhere or see anything if invalid login
    // Throwing an error so the try block's catch block in login.vue.onSuccess() fires
    throw new Error('User is not in database')
  }
}





// NAVIGATION
export const getNextInvoicePosition = async (id, direction = null) => {
  // Returns the existence in form of an invoice's position in the array of ids from search

  // Each time we go to get the invoice's position, we should update our stored invoice IDs
  // in local storage by re-searching
  const lsSearch = JSON.parse(sessionStorage.getItem('search'))
  // When manually routing to an invoice-details page, search in LS is null
  if (!lsSearch) {
    return 'search-is-null'
  }

  const invoiceIDs = await new FactoringAPI().getInvoiceIDsForInvoiceDetails(lsSearch.params)

  // Update local storage
  // Store position in case we need to reference our last known position
  sessionStorage.setItem('search', JSON.stringify({
    ...lsSearch,
    ids: invoiceIDs,
  }))

  CLog(`ID looking for: ${id}`)
  const invoicePosition = invoiceIDs.indexOf(id)
  CLog(`Invoice position: ${invoicePosition}`)

  /*
   * IF IDs have length:
   *   Is there a match in IDs and ID?
   *     NO: Load the invoice at the last known position
   *     YES: Are we going backward or forward?
   *       BACKWARD: Load the ID 1 index backward from the ID match
   *       FORWARD: Load the ID 1 index forward from the ID match
   * ELSE
   *   Load the passed in ID
   */

  // If IDs has length
  if (invoiceIDs.length) {
    // If there is a match in IDs with ID
    if (invoicePosition > -1) {
      let nextInvoiceIndex
      // Are we going backward or forward?

      // BACKWARD: invoice position - 1; if already at 0, BAIL
      if (direction === 'backward') {
        nextInvoiceIndex = invoicePosition - 1
        // If nextInvoiceIndex = -1, BAIL
        if (nextInvoiceIndex === -1) {
          CLog('Next index was -1')
          return 'at-beginning'
        }
      }
      // FORWARD: invoice position + 1; if already at IDs.length, BAIL
      else if (direction === 'forward') {
        nextInvoiceIndex = invoicePosition + 1
        // If nextInvoiceIndex is greater than the length of the ids from the current search
        if (nextInvoiceIndex > invoiceIDs.length - 1) {
          CLog('Next index was passed length of invoice IDs')
          return 'at-end'
        }
      }
      // This is caught when a user clicks on a load # of a pending item
      else {
        nextInvoiceIndex = invoicePosition
      }

      // Update LS with the incoming invoice position
      sessionStorage.setItem('search', JSON.stringify({
        ...JSON.parse(sessionStorage.getItem('search')),
        lastPosition: nextInvoiceIndex,
      }))

      // RETURN THE ID OF THE NEXT INVOICE USING THE NEXT INVOICE INDEX
      const nextInvoiceID = invoiceIDs[nextInvoiceIndex]
      // For debugging
      CLog(
        `%c INVOICE-DETAILS NAV INFO::
          INCOMING INVOICES:
            next invoice: ${nextInvoiceID}
            next invoice position: ${nextInvoiceIndex}
            previous invoice: ${id}
            previous invoice position: ${invoicePosition}
            search params: ${lsSearch.url}
        `,
        'color: purple; font-size: 16px;'
      )

      return nextInvoiceID
    }
    // If there isn't a match in IDs with ID
    // eslint-disable-next-line no-else-return
    else {
      // For debugging:
      CLog('Invoice ID was not found in search IDs')
      const lastPosition = JSON.parse(sessionStorage.getItem('search')).lastPosition
      CLog(`Last Position was: ${lastPosition}`)
      // We want to return the last known position if there isn't a match
      // We have to specifically look for undefined since array[empty index] === undefined
      // i.e. were at the end of the array of IDs and the last position was 1 index too far
      if (invoiceIDs[lastPosition] === undefined || !invoiceIDs[lastPosition]) {
        return invoiceIDs[0]
      }
      return invoiceIDs[lastPosition]
      // return lastPosition ? invoiceIDs[lastPosition] : invoiceIDs[0]
    }
  }
  // If IDs do not have length
  // eslint-disable-next-line no-else-return
  else {
    // For debugging:
    CLog('IDs is empty; Invoice ID was not found in search IDs')
    return 'ids-empty'
  }
}

export const goToNextInvoice = async (id, router, alertWarning, direction = null) => {
  const currentRouteID = window.location.pathname.replace('/invoice-details/', '')
  const debugObject = {
    currentInvoice: id,
    direction,
  }
  const lsSearch = JSON.parse(sessionStorage.getItem('search'))
  const nextInvoiceID = await getNextInvoicePosition(id, direction)
  debugObject.currentRouteID = currentRouteID
  debugObject.lsSearch = lsSearch
  debugObject.nextInvoice = nextInvoiceID

  // Alert combinations (next invoice position not found):
  let showNext = false
  let showPrevious = false
  const invoiceDetailsAlert = () => {
    debugObject.showNext = showNext
    debugObject.showPrevious = showPrevious
    alertWarning(true, showPrevious, showNext)
    CLog(debugObject)
  }

  switch (nextInvoiceID) {
    // This is the case when a user comes to invoice-details from universal search
    // I'm essentially disabling invoice navigation in this scenario
    case 'search-is-null':
      // For debugging
      console.log(
        `%c INVOICE-DETAILS NAV INFO::
          "search" in LS is null, routing to the ONE invoice
        `,
        'color: purple; font-size: 16px;'
      )
      // For debugging
      debugObject.nextInvoiceID = id
      debugObject.previousInvoice = null

      // Before routing, check if `id` is the same as the current page's id
      // (prevent duplicate navigation)
      // This also sends a user back to pending if they change the status of
      // an invoice after searching from universal search
      if (id === currentRouteID) {
        router.push({ name: 'pending' })
        return
      }

      router
        .push({
          name: 'invoice-details',
          params: {
            disabledNavigation: true,
            id: id.toString(),
          },
        })
        .catch((error) => {
          CError(error)
          captureSentryEvent(
            'Helpers: "GoToNextInvoice" from universal search',
            {
              config: error.config
                ? error.config
                : {},
              details: error,
              functionVariables: debugObject,
              response: error.response,
            }
          )
        })

      break

    // At beginning of IDs: hide alert, back to pending, next invoice
    case 'at-beginning':
      // For debugging
      console.log(
        `%c INVOICE-DETAILS NAV INFO::
          Navigation is already at the beginning
        `,
        'color: purple; font-size: 16px;'
      )
      showNext = true
      invoiceDetailsAlert()
      break

    // At end of IDs: hide alert, back to pending, previous invoice,
    case 'at-end':
      // For debugging
      console.log(
        `%c INVOICE-DETAILS NAV INFO::
          Navigation is already at the end
        `,
        'color: purple; font-size: 16px;'
      )
      showPrevious = true
      invoiceDetailsAlert()
      break

    // Cannot find ID in IDs, but IDs have length: hide alert, next invoice (loads 0)
    case 'id-not-in-ids':
      // For debugging
      console.log(
        `%c INVOICE-DETAILS NAV INFO::
          ID was not found in IDs (LS)
        `,
        'color: purple; font-size: 16px;'
      )
      showNext = true
      invoiceDetailsAlert()
      break

    // Cannot find ID in IDs and IDs do NOT have length: hide alert, back to pending
    case 'ids-empty':
      // For debugging
      console.log(
        `%c INVOICE-DETAILS NAV INFO::
          IDs is empty
        `,
        'color: purple; font-size: 16px;'
      )
      invoiceDetailsAlert()
      break
    // If a position for the next invoice is found
    default:
      // For debugging
      debugObject.nextInvoiceID = nextInvoiceID
      debugObject.previousInvoice = id

      router
        .push({
          name: 'invoice-details',
          params: { id: nextInvoiceID.toString() },
        })
        .catch((error) => {
          CError('GoToNextInvoice error', error)
          console.log(
            `
              nextInvoice:${nextInvoiceID},
              previousInvoice: ${id},
              routerID:${currentRouteID}
            `
          )
          captureSentryEvent(
            'Helpers: GoToNextInvoice"',
            {
              config: (error && error.config)
                ? error.config
                : '',
              details: error,
              functionVariables: debugObject,
              response: (error && error.response)
                ? error.response
                : '',
            }
          )
        })
  }
}

export const viewDocument = async (id, user) => {
  try {
    if (user === 'client') {
      window.open((await ClientDocument.get(id)).data.url)
    }
    else {
      window.open((await DebtorDocument.get(id)).data.url)
    }
  }
  catch (error) {
    captureSentryEvent(
      'Helpers: viewDocument',
      {
        config: error.config,
        details: error,
        id,
        response: error.response,
      }
    )
    CError(error)
  }
}

export const isProduction = () => {
  return process.env.VUE_APP_ENV === 'production'
}
